import Link from "next/link";
import { categoryBrowseHref } from "@/lib/category-data";
import { categoryLabel } from "@/lib/category-taxonomy";
import {
  formatInstallCountRow,
  installCountAriaLabel,
  visibleInstallCount,
} from "@/lib/format-count";
import { fmtDuration } from "@/lib/tls-format";

/** Visible on touch and keyboard alike; the old hover-only tooltip never was. */
export const LONG_TOKEN = "Long: trim first";

export type RowMetaToken = { kind: "duration" | "long" | "installs"; text: string };

/**
 * The row's facts, in order: duration, the trim note when the file runs past
 * the limit, then installs with its unit ("3,783 installs"). Zero installs
 * reads "NEW" (§02.9), the only meaning of that word in a row.
 */
export function rowMetaTokens(input: {
  downloads: number;
  duration?: number;
  needsTrimming?: boolean;
  /**
   * Related-list form (sound detail page): 1 to 24 installs is left out, so
   * the row never shows "2 installs" for a long-tail sound.
   */
  related?: boolean;
}): RowMetaToken[] {
  const { downloads, duration, needsTrimming, related } = input;
  const tokens: RowMetaToken[] = [];
  if (typeof duration === "number" && duration > 0) {
    tokens.push({ kind: "duration", text: fmtDuration(duration) });
  }
  if (needsTrimming) tokens.push({ kind: "long", text: LONG_TOKEN });
  const count = downloads ?? 0;
  if (!related || visibleInstallCount(count)) {
    tokens.push({
      kind: "installs",
      text: count === 0 ? formatInstallCountRow(0) : installCountAriaLabel(count),
    });
  }
  return tokens;
}

interface RowMetaProps {
  tokens: RowMetaToken[];
  /** DB category name; the row links it to its hub. Omit to leave it out. */
  category?: string | null;
  /** This row's preview is playing: the facts give way to "Playing". */
  isPlaying?: boolean;
  /** Library id for the Chime Studio handoff; without it the trim note is plain text. */
  soundId?: string;
  onCategoryClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

/**
 * The line under a row's title: category, then the facts. On a phone it is one
 * line that wraps between facts ("Memes  0:03  3,783 installs"), spaced rather
 * than dot-joined so a wrap never strands a separator; from 640px the facts
 * move to right-aligned tabular columns and the category stays under the
 * title. Both layouts come from the same nodes (globals.css, `.sc-meta`).
 */
export function RowMeta({ tokens, category, isPlaying = false, soundId, onCategoryClick }: RowMetaProps) {
  const needsTrimming = tokens.some((t) => t.kind === "long");
  const facts = isPlaying ? [{ kind: "playing" as const, text: "Playing" }] : tokens;
  return (
    <div className="sc-meta">
      {category && (
        <Link
          prefetch={false}
          href={categoryBrowseHref(category)}
          onClick={onCategoryClick}
          className="sc-cat tlsr-focus tls-touch-pad"
        >
          {categoryLabel(category)}
        </Link>
      )}
      <span
        className="sc-run"
        data-playing={isPlaying ? "" : undefined}
        data-long={needsTrimming ? "" : undefined}
      >
        {facts.map((token) => (
          <span key={token.kind} className="sc-fact" data-token={token.kind}>
            {token.kind === "long" && soundId ? (
              <Link
                prefetch={false}
                href={`/convert?sound=${encodeURIComponent(soundId)}`}
                className="tlsr-focus tls-touch-pad inline-flex min-h-6 items-center underline underline-offset-2"
                style={{ color: "var(--tls-fg-strong)" }}
              >
                {token.text}
              </Link>
            ) : (
              token.text
            )}
          </span>
        ))}
      </span>
    </div>
  );
}
